/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import assert from 'assert';

import {
  Component,
  Mixins,
  Vue,
} from 'vue-property-decorator';

import { assertExists } from '@/lib/assert';
import {
  ProductVariant,
  ProductVariantOption,
} from '@/models';

const PalettePanelProps = Vue.extend({
  props: {
    variant: {
      type: ProductVariant,
      required: true,
    },
    previewVariant: {
      type: ProductVariant,
    },
    value: {
      type: Object as () => ProductVariantOption,
      required: true,
    },
  },
});

// Keep in mind that a PalettePanel renders a palette for an option specified in `this.value`. Data
// in PalettePanel (e.g., optionIdx) are corresponding to this option.
@Component
export default class PalettePanel extends PalettePanelProps {
  // A product variant has many options represented in an array. This returns the index of the
  // option. The index is a constant for an option within a product. So it is computed once in
  // created().
  protected optionIdx = -1;

  // For each value in the option, valueVariants maps each value to the variants in the product that
  // has such value in the option.
  protected valueVariants = new Map</* value */string, ProductVariant[]>();

  public created() {
    const options = assertExists(this.variant.options);
    this.optionIdx = options.findIndex((option) => (option === this.value));
    assert.ok(this.optionIdx >= 0);

    // Initialize valueVariants.
    for (const variant of this.variant.product.variants) {
      const valueAtOption = (variant.options!)[this.optionIdx].value;
      const variants = this.valueVariants.get(valueAtOption);
      if (variants) {
        variants.push(variant);
      } else {
        this.valueVariants.set(valueAtOption, [variant]);
      }
    }
  }

  get previewValue() {
    if (this.previewVariant) {
      return (this.previewVariant.options!)[this.optionIdx].value;
    }
    return null;
  }

  // `optionVariants` contains all variants where values for all options are the same as `variant`
  // except the one for `value.option`.
  get optionVariants() {
    return this.variant.getOtherVariantsOnlyDifferFor(this.optionIdx);
  }

  protected emitSelectValue(value: string) {
    const variant = this.variantForValue(value);
    if (variant) {
      this.$emit('select-variant', variant);
    }
  }

  protected emitStartPreviewForValue(value: string) {
    const variant = this.variantForValue(value);
    if (variant) {
      this.$emit('preview-variant', variant);
    }
  }

  protected emitEndPreview() {
    this.$emit('preview-variant', null);
  }

  private variantForValue(value: string) {
    let variant = this.optionVariants.get(value);
    if (!variant) {
      const variants = this.valueVariants.get(value);
      if (variants) {
        variant = variants[0];
      }
    }
    return variant;
  }
}
